import { useMemo } from 'react'
import cx from 'classnames'
import filter from 'lodash/filter'
import { ResponseVariant } from 'workflow/Insights/Insights'

import { Column } from 'common/components/table'
import Tooltip from 'common/components/Tooltip'
import TableWidget from 'common/components/widget/tableWidget'
import { useAppSelector } from 'common/hooks/redux'
import useTableActions from 'common/hooks/useTableActions'
import { Info } from 'common/icons'
import Icon from 'common/icons/Icon'
import { TextWithLineBreaks } from 'common/variantsUtils'
import ActionCell from 'features/unifiedFlow/contentPage/components/ActionsCell'
import { CurrentUsersBanner } from 'features/unifiedFlow/contentPage/components/CurrentUsersBanner'
import {
  getIsOptimized,
  getIsPersonalized,
} from 'features/unifiedFlow/contentPage/helpers'
import {
  useContent,
  useSelectedComponentElement,
} from 'features/unifiedFlow/contentPage/hooks'
import { getWsTopic } from 'features/unifiedFlow/contentPage/hooks/websocketMessage/helpers'
import { useVariantLanguageContext } from 'features/unifiedFlow/contentPage/review/context/VariantLanguageContext'

import ApprovalsTableActionButtons from './ApprovalsTableActionButtons'

type ResponseVariantExtended = ResponseVariant & {
  isLoading: boolean
  isRefreshed: boolean
  category: string | undefined
  isHighlighted: boolean
}

type Props = {
  tweakSuccessVariantIds: string[]
  onSelectVariant: (variant: ResponseVariant) => void
}

const ApprovalsTable = ({ tweakSuccessVariantIds, onSelectVariant }: Props) => {
  const { content } = useContent()
  const { data: selectedElement } = useSelectedComponentElement()
  const { selectVariant, state } = useVariantLanguageContext()

  const subjectLines = useAppSelector(
    (state) => state.campaignStates.subjectLines
  )
  const variantsToBeApproved = useAppSelector(
    (state) => state.campaignStates.variantsToBeApproved
  )

  const variantsToBeApprovedFiltered = useMemo(() => {
    const mappedVariants = filter(subjectLines, ({ _id }) =>
      variantsToBeApproved.includes(_id)
    ).map((variant) => {
      let isLoading = false
      const isModifiedByUser = !!variant?.why_tweak
      const isSelected = state.selectedVariantCombinationId === variant._id
      if (content && selectedElement) {
        const tweakWsTopic = getWsTopic({
          action: 'tweakVariant',
          data: {
            contentId: content._id,
            elementId: selectedElement.element_id,
            variantId: variant._id,
          },
        })
        const rejectWsTopic = getWsTopic({
          action: 'rejectVariant',
          data: {
            contentId: content._id,
            elementId: selectedElement.element_id,
            variantId: variant._id,
          },
        })
        isLoading =
          content.ws_messages?.some(
            (message) =>
              message.wsTopic === rejectWsTopic ||
              message.wsTopic === tweakWsTopic
          ) || false
      }
      return {
        ...variant,
        isLoading,
        isRefreshed: tweakSuccessVariantIds.includes(variant._id),
        category: isModifiedByUser ? 'User modified' : undefined,
        isHighlighted: isSelected,
      }
    })
    return mappedVariants?.toSorted(
      (a, b) => (a.category ? 1 : 0) - (b.category ? 1 : 0)
    )
  }, [
    content,
    selectedElement,
    subjectLines,
    tweakSuccessVariantIds,
    variantsToBeApproved,
    state.selectedVariantCombinationId,
  ])

  const isFirstRowGrouped = !!variantsToBeApprovedFiltered[0]?.category

  const isApproveButtonDisabled = variantsToBeApprovedFiltered.some(
    (variant) => variant.isLoading
  )

  const elementId = selectedElement?.element_id
  const { onRequestChange } = useTableActions()

  const isPersonalized = getIsPersonalized(selectedElement)
  const isOptimized = getIsOptimized(selectedElement)

  const handleRowClick = (variant: ResponseVariantExtended) => {
    if (isPersonalized || isOptimized) {
      selectVariant({
        variantId: variant._id,
      })
      onSelectVariant(variant)
    }
  }

  const columns: Column<ResponseVariantExtended>[] = useMemo(
    () => [
      {
        Header: `Variants to approve (${variantsToBeApproved.length})`,
        accessor: 'text',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <div
            className={cx('flex ml-6 mt-4 items-center', {
              'text-base-700': row.original.isHighlighted,
              'text-gold-700': !row.original.isHighlighted,
            })}
          >
            <TextWithLineBreaks text={row.original.text} />
            {row.original.mutation && (
              <span className="ml-3 mr-3">
                <Tooltip overlay="This variant was generated by combining the top two performers of the campaign.">
                  <Icon
                    isDefaultColor={false}
                    className="text-white"
                    defaultSvg={Info}
                  />
                </Tooltip>
              </span>
            )}
          </div>
        ),
      },
      {
        Header: 'Actions',
        id: 'actions',
        accessor: '_id',
        disableSortBy: true,
        align: 'right',
        disableFilters: true,
        width: 140,
        className: 'flex-none',
        Cell: ({ row }) => (
          <ActionCell
            isTweaking={row.original.isLoading}
            isSuccessful={row.original.isRefreshed}
            onRequestChange={() => onRequestChange(row.original)}
          />
        ),
      },
      {
        accessor: 'category',
      },
    ],
    [onRequestChange, variantsToBeApproved.length]
  )

  return (
    <>
      <CurrentUsersBanner />
      <TableWidget.Widget<ResponseVariantExtended>
        key={elementId}
        data={variantsToBeApprovedFiltered}
        columns={columns}
        autoResetPage={false}
        onRowClick={handleRowClick}
        headerClassName={cx({
          'bg-transparent text-base-700': isFirstRowGrouped,
        })}
        initialState={{ groupBy: ['category'], hiddenColumns: ['category'] }}
      >
        <TableWidget.ActionButtons>
          <ApprovalsTableActionButtons
            variants={variantsToBeApprovedFiltered}
            isApproveButtonDisabled={isApproveButtonDisabled}
          />
        </TableWidget.ActionButtons>
      </TableWidget.Widget>
    </>
  )
}

export default ApprovalsTable
